import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { BannerComponent } from '../../../../shared/components/banner/banner.component';
import { SearchAndFiltrerComponent } from '../../../../shared/components/search-and-filtrer/search-and-filtrer.component';
import { CardDoctorComponent } from '../../../../shared/components/card-doctor/card-doctor.component';
import { DoctorSkeletonComponent } from '../../../../shared/components/doctor-skeleton/doctor-skeleton.component';
import { LocationAlertComponent } from '../../../../shared/components/location-alert/location-alert.component';
import {
  LocationService,
  LocationCoordinates,
} from '../../../../core/services/location.service';
import { ReferentielService } from '../../../../core/services/referentiel.service';
import { Doctor } from '../../../../core/interfaces/doctor.interface';
import { environment } from '../../../../../environments';
import { CountryStorageService } from '../../../../core/services/country.storage.service';
import { Country } from '../../../../core/interfaces/doctor.interface';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

interface DoctorsResponse {
  results: Doctor[];
  pagination: {
    totalItems: number;
    pageCount: number;
    currentPage: number;
    pageSize: number;
  };
}

@Component({
  selector: 'app-list-medecin',
  standalone: true,
  templateUrl: './list-medecin.component.html',
  styleUrls: ['./list-medecin.component.scss'],
  imports: [
    CommonModule,
    BannerComponent,
    SearchAndFiltrerComponent,
    CardDoctorComponent,
    DoctorSkeletonComponent,
    LocationAlertComponent,
    RouterModule,
  ],
})
export class ListMedecinComponent implements OnInit, OnDestroy {
  currentSection: 'medecins' | 'structures' | 'pharmacies' = 'medecins';
  currentTheme = environment.theme.default;
  bannnerLinks = [
    { label: 'Portail Public', href: '/' },
    { label: 'Médecins', href: undefined },
  ];

  // Données des médecins
  doctors: Doctor[] = [];
  loading = false;
  error: string | null = null;

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  // Filtres
  searchQuery = '';
  searchTownId = '';
  searchCountryId: any;
  searchSpeciality = '';
  selectedLanguage = '';
  selectedService = '';
  selectedInsurer = '';

  latitude: number | null = null;
  longitude: number | null = null;

  selectedCountry: Country | null = null;
  private destroy$ = new Subject<void>();
  private initialParamsLoaded = false;
  /** Première émission de location$ : on ne recharge pas (déjà chargé par queryParams). */
  private initialLocationEmit = true;

  constructor(
    private referentielService: ReferentielService,
    private route: ActivatedRoute,
    private router: Router,
    private countryStorage: CountryStorageService,
    private locationService: LocationService
  ) {}

  ngOnInit(): void {
    // Détection du type de section (medecins / structures / pharmacies)
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          if (this.router.url.includes('/structures')) {
            this.currentSection = 'structures';
          } else if (this.router.url.includes('/pharmacies')) {
            this.currentSection = 'pharmacies';
          } else {
            this.currentSection = 'medecins';
          }
        }
      });

    this.countryStorage.country$
      .pipe(takeUntil(this.destroy$))
      .subscribe((country) => {
        this.selectedCountry = country;
        if (country) {
          this.searchCountryId = country.id;
        }
      });

    // Localisation : debounce 2s pour éviter rechargements multiples
    this.locationService.location$
      .pipe(
        debounceTime(2000),
        distinctUntilChanged((prev, curr) => {
          if (!prev || !curr) return prev === curr;
          const distance = this.calculateDistance(
            prev.latitude,
            prev.longitude,
            curr.latitude,
            curr.longitude
          );
          return distance < 0.1; // 100 m
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((location) => {
        if (location) {
          this.latitude = location.latitude;
          this.longitude = location.longitude;
          // Ne pas recharger au 1er emit (liste déjà chargée par queryParams) ; seulement si la position change ensuite
          if (!this.initialLocationEmit) {
            this.loadDoctors();
          } else {
            this.initialLocationEmit = false;
          }
        } else {
          this.latitude = null;
          this.longitude = null;
        }
      });

    this.loadLocationFromStorage();
    this.loadUrlParams();
    // Un seul chargement initial : fait par loadUrlParams au 1er emit de queryParams
  }

  /**
   * Calculer la distance entre deux points en kilomètres (formule de Haversine)
   */
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371; // Rayon de la Terre en km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) *
        Math.cos(this.deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLocationActivated(coordinates: LocationCoordinates): void {
    this.latitude = coordinates.latitude;
    this.longitude = coordinates.longitude;
    this.loadDoctors();
  }

  loadLocationFromStorage(): void {
    const location = this.locationService.loadLocationFromStorage();
    if (location) {
      this.latitude = location.latitude;
      this.longitude = location.longitude;
    }
  }

  private loadUrlParams(): void {
    this.route.queryParams
      .pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe((params) => {
        const newSearchQuery = params['query'] || '';
        const newSearchTownId = params['townId'] || params['town'] || '';
        const newSearchSpeciality = params['specialisationIds'] || '';
        const newSelectedLanguage = params['language'] || '';
        const newSelectedService = params['service'] || '';
        const newSelectedInsurer = params['insurer'] || '';
        const newPage = parseInt(params['page'] as string, 10) || 1;

        const hasParamsChanged =
          this.searchQuery !== newSearchQuery ||
          this.searchTownId !== newSearchTownId ||
          this.searchSpeciality !== newSearchSpeciality ||
          this.selectedLanguage !== newSelectedLanguage ||
          this.selectedService !== newSelectedService ||
          this.selectedInsurer !== newSelectedInsurer;

        this.searchQuery = newSearchQuery;
        this.searchTownId = newSearchTownId;
        this.searchSpeciality = newSearchSpeciality;
        this.selectedLanguage = newSelectedLanguage;
        this.selectedService = newSelectedService;
        this.selectedInsurer = newSelectedInsurer;
        this.currentPage = newPage;

        // Premier emit = chargement initial unique ; ensuite seulement si les filtres changent
        if (!this.initialParamsLoaded) {
          this.initialParamsLoaded = true;
          this.loadDoctors();
        } else if (hasParamsChanged) {
          this.currentPage = 1;
          this.loadDoctors();
        }
      });
  }

  loadDoctors(): void {
    this.loading = true;
    this.error = null;

    const searchParams = {
      limit: this.pageSize,
      page: this.currentPage,
      query: this.searchQuery || undefined,
      townId: this.searchTownId ? Number(this.searchTownId) : undefined,
      countryId: this.searchCountryId ? this.searchCountryId : undefined,
      specialisationIds: this.searchSpeciality || undefined,
      languageIds: this.selectedLanguage || undefined,
      serviceAvailableIds: this.selectedService || undefined,
      insurerIds: this.selectedInsurer || undefined,
      latitude: this.latitude || undefined,
      longitude: this.longitude || undefined,
    };

    this.referentielService.getDoctors(searchParams).subscribe({
      next: (response: DoctorsResponse) => {
        this.doctors = response.results.filter((el) => el.eyoneExternalId);
        this.totalItems = response.pagination.totalItems;
        this.totalPages = response.pagination.pageCount;
        this.currentPage = response.pagination.currentPage;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des médecins:', error);
        this.error = 'Erreur lors du chargement des médecins';
        this.loading = false;
      },
    });
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updateUrlParams();
      this.loadDoctors();
    }
  }

  onSearch(): void {
    this.currentPage = 1;
    this.updateUrlParams();
    this.loadDoctors();
  }

  private updateUrlParams(): void {
    const queryParams: any = {
      page: this.currentPage > 1 ? this.currentPage : undefined,
    };

    if (this.searchQuery) {
      queryParams.query = this.searchQuery;
    }
    if (this.searchTownId) {
      queryParams.town = this.searchTownId;
    }
    if (this.selectedLanguage) {
      queryParams.language = this.selectedLanguage;
    }
    if (this.selectedService) {
      queryParams.service = this.selectedService;
    }
    if (this.selectedInsurer) {
      queryParams.insurer = this.selectedInsurer;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;

    if (this.totalPages <= maxVisiblePages) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(
        1,
        this.currentPage - Math.floor(maxVisiblePages / 2)
      );
      let end = Math.min(this.totalPages, start + maxVisiblePages - 1);

      if (end - start + 1 < maxVisiblePages) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  }
}
